import { PROFILE_TEMPLATES } from '@retracedgmbh/data-points-lib';

/**
 * Amfori credential data-point ids. Single source of truth is
 * `PROFILE_TEMPLATES.AMFORI_INTEGRATION.dataPointIds` in
 * `@retracedgmbh/data-points-lib`.
 */
const AMFORI_INTEGRATION_DATA_POINT_IDS =
  PROFILE_TEMPLATES.AMFORI_INTEGRATION.dataPointIds;

/**
 * Semantic accessors for the amfori data-point ids.
 *
 * Order MUST match `PROFILE_TEMPLATES.AMFORI_INTEGRATION.dataPointIds`.
 *
 * Indices 0-3 are the brand-side credential data-points (consumed by
 * `AmforiCredentialsService`). Indices 4-6 are the supplier-side identifiers
 * imported during a sync and consumed by `AmforiSupplierMatcherService`. Index 7
 * is the brand-side revoked-at marker (stamped when amfori revokes the
 * credentials during a sync; cleared when new credentials are saved).
 */
export const AMFORI_DATA_POINT_IDS = {
  CLIENT_ID: AMFORI_INTEGRATION_DATA_POINT_IDS[0],
  CLIENT_SECRET: AMFORI_INTEGRATION_DATA_POINT_IDS[1],
  CREDENTIALS_VERIFIED_AT: AMFORI_INTEGRATION_DATA_POINT_IDS[2],
  MEMBER_ID: AMFORI_INTEGRATION_DATA_POINT_IDS[3],
  BUSINESS_PARTNER_ID: AMFORI_INTEGRATION_DATA_POINT_IDS[4],
  SITE_ID: AMFORI_INTEGRATION_DATA_POINT_IDS[5],
  LAST_SYNCED_AT: AMFORI_INTEGRATION_DATA_POINT_IDS[6],
  CREDENTIALS_REVOKED_AT: AMFORI_INTEGRATION_DATA_POINT_IDS[7],
} as const;

export const AMFORI_DATA_POINT_ID_LIST = AMFORI_INTEGRATION_DATA_POINT_IDS;

/**
 * Sentinel written into the supplier's `amfori_site_id` answer `_meta` when a
 * brand explicitly UNLINKS the site id (DELTA-2254). It marks the empty site id
 * as a deliberate manual clear — not merely "never synced" — so the next sync's
 * matcher classifies the supplier as BLOCKED (never auto-re-matched) until a
 * human re-links it. See `AmforiSupplierMatcherService.classify` and
 * `getExistingAmforiSiteIdDb`.
 */
export const AMFORI_SITE_ID_UNLINKED_META_KEY = 'unlinked';

/**
 * The persistence table that backs every self-reported data-point answer.
 * Amfori credentials are persisted as 4 rows in this table, one per
 * `AMFORI_DATA_POINT_IDS` value.
 */
export const AMFORI_CREDENTIALS_TABLE = 'COMPANIES_SERVICE_DATA_POINTS_ANSWERS';

/**
 * Amfori SSO realm token endpoint. We mint a short-lived JWT here every time
 * the user persists / verifies their credentials so we never store a token.
 */
export const AMFORI_TOKEN_URL =
  'https://sso.amfori.org/auth/realms/amfori/protocol/openid-connect/token';

export const AMFORI_USER_AGENT =
  'Retraced-Platform/1.0 (+https://retraced.com)';

/**
 * Base URL for the amfori supply-chain (transversal) API. Master-data reads
 * (members -> business partners -> sites) hang off this path.
 *
 * Same host as the data API (`AMFORI_DATA_API_BASE_URL`), different path segment
 * (`/transversal/...` vs `/social/...`). Validated against the live tenant: the
 * `member` / `business-partner` / `site` resources are flat, top-level, and use
 * the same `{ result, paginationInfo }` envelope + `pageNumber`/`pageSize` paging
 * as the data endpoints.
 */
export const AMFORI_SUPPLY_CHAIN_BASE_URL =
  'https://api.insights.amfori.org/transversal/v2.0/supply-chain';

/**
 * Base URL for the amfori data (insights) API host that serves the BSCI
 * data-fetching endpoints (`/social/v2.0/supply-chain/...`). Distinct from the
 * SSO host (`AMFORI_TOKEN_URL`) and the (placeholder) transversal host above.
 * Validated against the live tenant during US3.4 discovery (§1 + the sub-task-3
 * probe). Endpoints append their own path, e.g.
 * `/social/v2.0/supply-chain/monitoring`.
 */
export const AMFORI_DATA_API_BASE_URL = 'https://api.insights.amfori.org';

/**
 * Datapoints phase fan-out granularity: the number of monitorings whose
 * datapoints one child job pulls + writes. Roughly one amfori page worth of
 * audits at the current ~539-datapoints-per-audit / 15k-per-page sizing. A
 * single audit's full datapoint set is always owned by exactly one child, so
 * the datapoint-writer's per-`digitized_audits` write never races.
 */
export const AMFORI_DATAPOINTS_MONITORINGS_PER_BATCH = 25;

/**
 * How long a sync run may stay `RUNNING` before the reaper reclaims it as
 * `FAILED`. A normal run settles in minutes; a row still RUNNING past this is
 * treated as orphaned (worker crash / lost master job) so it stops blocking the
 * single-RUNNING-per-company guard. Reaped by the hourly `cron-amfori-reap-stale-runs`.
 */
export const AMFORI_SYNC_STALE_TIMEOUT_MS = 2 * 60 * 60 * 1000;

/**
 * Which amfori datapoint labels supply the audit-level `currency` / `year` used
 * to normalize `currency_with_year` metrics (e.g. minimum wage). amfori
 * datapoint records carry no audit-level currency/year, so the datapoints phase
 * resolves them from these designated datapoints (pinned against live amfori):
 *
 * - **currency** — the `'Local currency'` datapoint (a `predefined_value` whose
 *   `value` is the `Currency.<ISO>` enum, e.g. `Currency.CNY`). The resolver
 *   strips the `Currency.` prefix to the ISO code (`CNY`).
 * - **year** — the audit's `'On-Site Activity End Date'` (a `date` datapoint
 *   `{year, month, day}`); its `year` is the reporting year for the wage values.
 */
export const AMFORI_DATAPOINT_METADATA_LABELS: {
  readonly currency: readonly string[];
  readonly year: readonly string[];
} = {
  currency: ['Local currency'],
  year: ['On-Site Activity End Date'],
};

/**
 * Minimum combined similarity score (0-100) a hybrid-search candidate must
 * reach for `AmforiSupplierMatcherService` to surface it as a suggestion.
 * `>=` clears the bar. Below it, the matcher falls through to UNMATCHED.
 */
export const HYBRID_SEARCH_MATCH_THRESHOLD = 85;

/**
 * Ambiguity window (in score points, 0-100) used by the matcher's hybrid-search
 * step. When two or more ELIGIBLE candidates score within this window of the
 * top eligible candidate, the match is too close to call automatically and the
 * matcher returns `UNMATCHED` with `reason: 'AMBIGUOUS'` rather than guessing.
 *
 * Boundary: a candidate `topScore - candidateScore <= MATCH_AMBIGUITY_WINDOW`
 * counts as within the window (a 2-point gap is ambiguous; a 3-point gap is
 * decisive). When the window catches a genuine near-tie, the matcher first
 * tries to break it by the amfori site's country before giving up as ambiguous.
 */
export const MATCH_AMBIGUITY_WINDOW = 2;

/**
 * Legacy master-job name. No longer enqueued (the sync now chains its phases
 * directly — see {@link AMFORI_SYNC_MASTER_DATA_JOB_NAME}); retained only as the
 * generic `failedPhase` label the stale-run reaper stamps on a reclaimed run,
 * where no single phase is to blame. Mirrors `JOB_NAMES.AMFORI_SYNC_FULL`.
 */
export const AMFORI_SYNC_FULL_JOB_NAME = 'amfori:sync.full';

/**
 * Name of the FIRST phase job the orchestrator enqueues to kick off a run. The
 * sync is a strict sequential chain (master-data → monitorings → datapoints →
 * findings → zero-tolerance → finalize); each phase enqueues the next on
 * success, so the orchestrator only starts the head of the chain. Mirrors
 * `JOB_NAMES.AMFORI_SYNC_MASTER_DATA` — kept here so the module can enqueue it
 * without importing `@src/*`.
 */
export const AMFORI_SYNC_MASTER_DATA_JOB_NAME = 'amfori:sync.master_data';

/**
 * Retry budget passed on the kickoff (master-data) enqueue. Mirrors the backend
 * `AMFORI_PHASE_MAX_ATTEMPTS` — kept here so the module can enqueue without
 * importing `@src/*`. MUST be passed explicitly: the job queue defaults an
 * omitted `maxAttempts` to its `defaultMaxAttempts` (10 in prod) and that wins
 * over the per-job registration, so leaving it off would run master-data at 10.
 */
export const AMFORI_PHASE_MAX_ATTEMPTS = 6;

/**
 * TTL (seconds) applied to every per-run cache entry written during a sync.
 * Generous enough to outlive a long sync, short enough that abandoned runs
 * self-evict if `cleanupRun` is never reached (e.g. a hard worker crash).
 * 24h.
 */
export const AMFORI_SYNC_CACHE_TTL_SECONDS = 60 * 60 * 24;

/** Root namespace for every amfori-sync cache key. */
const AMFORI_SYNC_CACHE_ROOT = 'amfori:sync';

/**
 * Builds a per-run cache key under the run's namespace.
 * Shape: `amfori:sync:<syncHistoryId>:<suffix>`.
 */
export const buildAmforiSyncCacheKey = (
  syncHistoryId: string,
  suffix: string,
): string => `${AMFORI_SYNC_CACHE_ROOT}:${syncHistoryId}:${suffix}`;

/**
 * Builds the step-log buffer key for a single phase within a run.
 * Shape: `amfori:sync-log:<syncHistoryId>:<jobName>`.
 *
 * The Cache abstraction has no Redis list ops, so each buffer holds a JSON
 * array of `AmforiSyncStepLogEvent` written via get+set.
 */
export const buildAmforiSyncStepLogKey = (
  syncHistoryId: string,
  jobName: string,
): string => `amfori:sync-log:${syncHistoryId}:${jobName}`;

/**
 * Builds the tracker-set key for a run. The tracker holds a JSON array of every
 * cache key written for the run so `cleanupRun` can delete them without relying
 * on `SCAN`/`KEYS` (which the Cache abstraction does not expose).
 * Shape: `amfori:sync:<syncHistoryId>:__keys`.
 */
export const buildAmforiSyncTrackerKey = (syncHistoryId: string): string =>
  buildAmforiSyncCacheKey(syncHistoryId, '__keys');

/* ------------------------------------------------------------------------- *
 * amfori BSCI import reference constants (US3.4 sub-task 3)
 * ------------------------------------------------------------------------- */

/**
 * Value-type tag for an amfori BSCI datapoint, parallel to
 * `BsciDatapointValueType` in `modules/audits/constants.ts`.
 * `'currency_with_year'` means amfori stores a single composite value that the
 * datapoint-writer splits across `*_amount` + `*_currency` + `*_year`.
 */
export type AmforiBsciDatapointValueType = 'number' | 'currency_with_year';

/**
 * Maps an amfori English question label to a Retraced standardized datapoint.
 * Parallel to `BSCI_DATAPOINT_MAP`, but keyed on amfori's `amforiLabel`.
 */
export interface AmforiBsciDatapointMapping {
  readonly amforiLabel: string;
  readonly dataPointId: string;
  readonly valueType: AmforiBsciDatapointValueType;
  readonly category: string;
}

/**
 * The 58 amfori BSCI datapoint labels we ingest, each mapped to its Retraced
 * datapoint id, value type, and category. Generated from sheet 1 of
 * `amfori-bsci-datapoint-map.xlsx` (scripts/tmp/generate-amfori-question-map.mjs).
 */
export const AMFORI_BSCI_DATAPOINT_MAP: readonly AmforiBsciDatapointMapping[] =
  [
    {
      amforiLabel: 'Total workforce',
      dataPointId: 'total_number_of_workers',
      valueType: 'number',
      category: 'Key Metrics',
    },
    {
      amforiLabel: 'Male workers',
      dataPointId: 'number_of_male_workers',
      valueType: 'number',
      category: 'Workers',
    },
    {
      amforiLabel: 'Female workers',
      dataPointId: 'number_of_female_workers',
      valueType: 'number',
      category: 'Workers',
    },
    {
      amforiLabel: 'Permanent workers - Male',
      dataPointId: 'number_of_male_permanent_workers',
      valueType: 'number',
      category: 'Employment type',
    },
    {
      amforiLabel: 'Permanent workers - Female',
      dataPointId: 'number_of_female_permanent_workers',
      valueType: 'number',
      category: 'Employment type',
    },
    {
      amforiLabel: 'Temporary workers - Male',
      dataPointId: 'number_of_male_temporary_workers',
      valueType: 'number',
      category: 'Employment type',
    },
    {
      amforiLabel: 'Temporary workers - Female',
      dataPointId: 'number_of_female_temporary_workers',
      valueType: 'number',
      category: 'Employment type',
    },
    {
      amforiLabel: 'Seasonal workers - Male',
      dataPointId: 'number_of_male_seasonal_workers',
      valueType: 'number',
      category: 'Seasonal',
    },
    {
      amforiLabel: 'Seasonal workers - Female',
      dataPointId: 'number_of_female_seasonal_workers',
      valueType: 'number',
      category: 'Seasonal',
    },
    {
      amforiLabel: 'Management - Male',
      dataPointId: 'num_of_male_managers',
      valueType: 'number',
      category: 'Management',
    },
    {
      amforiLabel: 'Management - Female',
      dataPointId: 'num_of_female_managers',
      valueType: 'number',
      category: 'Management',
    },
    {
      amforiLabel: 'Apprentices - Male',
      dataPointId: 'number_of_male_trainees_apprentices_intern_workers',
      valueType: 'number',
      category: 'Apprentices',
    },
    {
      amforiLabel: 'Apprentices - Female',
      dataPointId: 'number_of_female_trainees_apprentices_intern_workers',
      valueType: 'number',
      category: 'Apprentices',
    },
    {
      amforiLabel: 'Workers on probation - Male',
      dataPointId: 'number_of_male_workers_under_probation',
      valueType: 'number',
      category: 'Probation',
    },
    {
      amforiLabel: 'Workers on probation - Female',
      dataPointId: 'number_of_female_workers_under_probation',
      valueType: 'number',
      category: 'Probation',
    },
    {
      amforiLabel: 'Workers with night shift - Male',
      dataPointId: 'number_of_male_night_shift_workers',
      valueType: 'number',
      category: 'Night shift',
    },
    {
      amforiLabel: 'Workers with night shift - Female',
      dataPointId: 'number_of_female_night_shift_workers',
      valueType: 'number',
      category: 'Night shift',
    },
    {
      amforiLabel: 'Workers with disabilities - Male',
      dataPointId: 'number_of_male_workers_with_disability',
      valueType: 'number',
      category: 'Disabilities',
    },
    {
      amforiLabel: 'Workers with disabilities - Female',
      dataPointId: 'number_of_female_workers_with_disability',
      valueType: 'number',
      category: 'Disabilities',
    },
    {
      amforiLabel: 'Domestic migrant workers - Male',
      dataPointId: 'number_of_male_domestic_migrant_workers',
      valueType: 'number',
      category: 'Migrant workers',
    },
    {
      amforiLabel: 'Domestic migrant workers - Female',
      dataPointId: 'number_of_female_domestic_migrant_workers',
      valueType: 'number',
      category: 'Migrant workers',
    },
    {
      amforiLabel: 'Foreign migrant workers - Male',
      dataPointId: 'number_of_male_foreign_migrant_workers',
      valueType: 'number',
      category: 'Migrant workers',
    },
    {
      amforiLabel: 'Foreign migrant workers - Female',
      dataPointId: 'number_of_female_foreign_migrant_workers',
      valueType: 'number',
      category: 'Migrant workers',
    },
    {
      amforiLabel: 'Workers hired directly - Male',
      dataPointId: 'number_of_male_workers_hired_directly',
      valueType: 'number',
      category: 'Hiring type',
    },
    {
      amforiLabel: 'Workers hired directly - Female',
      dataPointId: 'number_of_female_workers_hired_directly',
      valueType: 'number',
      category: 'Hiring type',
    },
    {
      amforiLabel: 'Workers hired indirectly - Male',
      dataPointId: 'number_of_male_workers_hired_indirectly',
      valueType: 'number',
      category: 'Hiring type',
    },
    {
      amforiLabel: 'Workers hired indirectly - Female',
      dataPointId: 'number_of_female_workers_hired_indirectly',
      valueType: 'number',
      category: 'Hiring type',
    },
    {
      amforiLabel: 'Unionised workers - Male',
      dataPointId: 'number_of_male_unionised_workers',
      valueType: 'number',
      category: 'Union membership',
    },
    {
      amforiLabel: 'Unionised workers - Female',
      dataPointId: 'number_of_female_unionised_workers',
      valueType: 'number',
      category: 'Union membership',
    },
    {
      amforiLabel: 'Workers under CBA - Male',
      dataPointId: 'number_of_male_workers_under_cba',
      valueType: 'number',
      category: 'CBA coverage',
    },
    {
      amforiLabel: 'Workers under CBA - Female',
      dataPointId: 'number_of_female_workers_under_cba',
      valueType: 'number',
      category: 'CBA coverage',
    },
    {
      amforiLabel: 'Pregnant workers',
      dataPointId: 'number_of_pregnant_or_breastfeeding_workers',
      valueType: 'number',
      category: 'Pregnancy and parental leave',
    },
    {
      amforiLabel: 'Workers on parental leave - Male',
      dataPointId: 'number_of_male_workers_on_parental_leave',
      valueType: 'number',
      category: 'Pregnancy and parental leave',
    },
    {
      amforiLabel: 'Workers on parental leave - Female',
      dataPointId: 'number_of_female_workers_on_parental_leave',
      valueType: 'number',
      category: 'Pregnancy and parental leave',
    },
    {
      amforiLabel: 'Minimum wage agreed on CBA in local currency',
      dataPointId: 'minimum_wage_agreed_on_cba_local_currency',
      valueType: 'currency_with_year',
      category: 'Wages',
    },
    {
      amforiLabel: 'Legal minimum wage in local currency',
      dataPointId: 'mininum_wage_local_currency',
      valueType: 'currency_with_year',
      category: 'Key Metrics',
    },
    {
      amforiLabel: 'Lowest wage paid for regular work at the site',
      dataPointId: 'lowest_wage_paid_regular_work',
      valueType: 'currency_with_year',
      category: 'Key Metrics',
    },
    {
      amforiLabel: 'Calculated living wage in local currency',
      dataPointId: 'living_wage_local_currency',
      valueType: 'currency_with_year',
      category: 'Key Metrics',
    },
    {
      amforiLabel: 'Total sample',
      dataPointId: 'payroll_how_many_workers_were_sampled',
      valueType: 'number',
      category: 'Key Metrics',
    },
    {
      amforiLabel: 'Sample - Male',
      dataPointId: 'payroll_male_workers_sampled',
      valueType: 'number',
      category: 'Sample',
    },
    {
      amforiLabel: 'Sample - Female',
      dataPointId: 'payroll_female_workers_sampled',
      valueType: 'number',
      category: 'Sample',
    },
    {
      amforiLabel: 'Sample - Non-binary',
      dataPointId: 'payroll_diverse_workers_sampled',
      valueType: 'number',
      category: 'Sample',
    },
    {
      amforiLabel: 'Non-binary workers',
      dataPointId: 'number_of_diverse_workers',
      valueType: 'number',
      category: 'Gender',
    },
    {
      amforiLabel: 'Permanent workers - Non-binary',
      dataPointId: 'number_of_diverse_permanent_workers',
      valueType: 'number',
      category: 'Employment type',
    },
    {
      amforiLabel: 'Temporary workers - Non-binary',
      dataPointId: 'number_of_diverse_temporary_workers',
      valueType: 'number',
      category: 'Employment type',
    },
    {
      amforiLabel: 'Seasonal workers - Non-binary',
      dataPointId: 'number_of_diverse_seasonal_workers',
      valueType: 'number',
      category: 'Seasonal',
    },
    {
      amforiLabel: 'Management - Non-binary',
      dataPointId: 'num_of_diverse_managers',
      valueType: 'number',
      category: 'Management',
    },
    {
      amforiLabel: 'Apprentices - Non-binary',
      dataPointId: 'number_of_diverse_trainees_apprentices_intern_workers',
      valueType: 'number',
      category: 'Apprentices',
    },
    {
      amforiLabel: 'Workers on probation - Non-binary',
      dataPointId: 'number_of_diverse_workers_under_probation',
      valueType: 'number',
      category: 'Probation',
    },
    {
      amforiLabel: 'Workers with night shift - Non-binary',
      dataPointId: 'number_of_diverse_night_shift_workers',
      valueType: 'number',
      category: 'Night shift',
    },
    {
      amforiLabel: 'Workers with disabilities - Non-binary',
      dataPointId: 'number_of_diverse_workers_with_disability',
      valueType: 'number',
      category: 'Disabilities',
    },
    {
      amforiLabel: 'Domestic migrant workers - Non-binary',
      dataPointId: 'number_of_diverse_domestic_migrant_workers',
      valueType: 'number',
      category: 'Migrant workers',
    },
    {
      amforiLabel: 'Foreign migrant workers - Non-binary',
      dataPointId: 'number_of_diverse_foreign_migrant_workers',
      valueType: 'number',
      category: 'Migrant workers',
    },
    {
      amforiLabel: 'Workers hired directly - Non-binary',
      dataPointId: 'number_of_diverse_workers_hired_directly',
      valueType: 'number',
      category: 'Hiring type',
    },
    {
      amforiLabel: 'Workers hired indirectly - Non-binary',
      dataPointId: 'number_of_diverse_workers_hired_indirectly',
      valueType: 'number',
      category: 'Hiring type',
    },
    {
      amforiLabel: 'Unionised workers - Non-binary',
      dataPointId: 'number_of_diverse_unionised_workers',
      valueType: 'number',
      category: 'Union membership',
    },
    {
      amforiLabel: 'Workers under CBA - Non-binary',
      dataPointId: 'number_of_diverse_workers_under_cba',
      valueType: 'number',
      category: 'CBA coverage',
    },
    {
      amforiLabel: 'Workers on parental leave - Non-binary',
      dataPointId: 'number_of_diverse_workers_on_parental_leave',
      valueType: 'number',
      category: 'Pregnancy and parental leave',
    },
  ];

/**
 * amfori delivers the ~50 workforce-headcount datapoints inside the
 * **Employment Structure** composite grid, keyed by short column codes rather
 * than the top-level `amforiLabel`s above (only the 8 wage/sample datapoints
 * arrive as top-level datapoints). This maps each composite column code to its
 * Retraced `dataPointId`; the decoder uses it to pull headcount values out of
 * `external_raw_payload.composites` into the mapped buckets.
 *
 * Code scheme: gender prefix `M_` / `F_` / `NB_` = male / female / diverse;
 * ungendered totals are `TOT_WR` (workforce) and `PRGNT_WR` (pregnant). Every
 * `dataPointId` here MUST exist in `AMFORI_BSCI_DATAPOINT_MAP` (asserted in the
 * unit test). See `docs/EXTENDING_AMFORI_SYNC.md`.
 */
export const AMFORI_BSCI_COMPOSITE_CODE_MAP: Readonly<Record<string, string>> =
  {
    // Totals
    TOT_WR: 'total_number_of_workers',
    M_TOT_WR: 'number_of_male_workers',
    F_TOT_WR: 'number_of_female_workers',
    NB_TOT_WR: 'number_of_diverse_workers',
    // Permanent
    M_PER_WR: 'number_of_male_permanent_workers',
    F_PER_WR: 'number_of_female_permanent_workers',
    NB_PER_WR: 'number_of_diverse_permanent_workers',
    // Temporary
    M_TMP_WR: 'number_of_male_temporary_workers',
    F_TMP_WR: 'number_of_female_temporary_workers',
    NB_TMP_WR: 'number_of_diverse_temporary_workers',
    // Seasonal
    M_SEAS_WR: 'number_of_male_seasonal_workers',
    F_SEAS_WR: 'number_of_female_seasonal_workers',
    NB_SEAS_WR: 'number_of_diverse_seasonal_workers',
    // Management
    M_MGMT_WR: 'num_of_male_managers',
    F_MGMT_WR: 'num_of_female_managers',
    NB_MGMT_WR: 'num_of_diverse_managers',
    // Apprentices / trainees / interns
    M_APPR_WR: 'number_of_male_trainees_apprentices_intern_workers',
    F_APPR_WR: 'number_of_female_trainees_apprentices_intern_workers',
    NB_APPR_WR: 'number_of_diverse_trainees_apprentices_intern_workers',
    // Probation
    M_PROB_WR: 'number_of_male_workers_under_probation',
    F_PROB_WR: 'number_of_female_workers_under_probation',
    NB_PROB_WR: 'number_of_diverse_workers_under_probation',
    // Night shift
    M_NGT_WR: 'number_of_male_night_shift_workers',
    F_NGT_WR: 'number_of_female_night_shift_workers',
    NB_NGT_WR: 'number_of_diverse_night_shift_workers',
    // Disabilities
    M_DIS_WR: 'number_of_male_workers_with_disability',
    F_DIS_WR: 'number_of_female_workers_with_disability',
    NB_DIS_WR: 'number_of_diverse_workers_with_disability',
    // Domestic migrant
    M_NAT_MIG_WR: 'number_of_male_domestic_migrant_workers',
    F_NAT_MIG_WR: 'number_of_female_domestic_migrant_workers',
    NB_NAT_MIG_WR: 'number_of_diverse_domestic_migrant_workers',
    // Foreign migrant
    M_FRGN_MIG_WR: 'number_of_male_foreign_migrant_workers',
    F_FRGN_MIG_WR: 'number_of_female_foreign_migrant_workers',
    NB_FRGN_MIG_WR: 'number_of_diverse_foreign_migrant_workers',
    // Hired directly
    M_DIR_WR: 'number_of_male_workers_hired_directly',
    F_DIR_WR: 'number_of_female_workers_hired_directly',
    NB_DIR_WR: 'number_of_diverse_workers_hired_directly',
    // Hired indirectly
    M_INDIR_WR: 'number_of_male_workers_hired_indirectly',
    F_INDIR_WR: 'number_of_female_workers_hired_indirectly',
    NB_INDIR_WR: 'number_of_diverse_workers_hired_indirectly',
    // Unionised
    M_UNION_WR: 'number_of_male_unionised_workers',
    F_UNION_WR: 'number_of_female_unionised_workers',
    NB_UNION_WR: 'number_of_diverse_unionised_workers',
    // Under CBA
    M_CBA_WR: 'number_of_male_workers_under_cba',
    F_CBA_WR: 'number_of_female_workers_under_cba',
    NB_CBA_WR: 'number_of_diverse_workers_under_cba',
    // Parental leave
    M_PARENT_WR: 'number_of_male_workers_on_parental_leave',
    F_PARENT_WR: 'number_of_female_workers_on_parental_leave',
    NB_PARENT_WR: 'number_of_diverse_workers_on_parental_leave',
    // Pregnant / breastfeeding (ungendered)
    PRGNT_WR: 'number_of_pregnant_or_breastfeeding_workers',
  };

/**
 * Maps every BSCI finding-producing `questionKey` to exactly one Retraced
 * impact_area_id (strictly 1:1 — no fan-out). Enumerated from amfori's
 * `monitoring-datapoint-template` (BSCI initiative) and frozen at build time.
 * Covers BOTH finding-key schemes amfori delivers audit findings under, keyed
 * on the same 13 Performance Areas:
 *   - the audit scheme — `MAN_*`, `MAN_SPA_*`, `FU_MAN_*` (`…PA<n>_<sub>`);
 *   - the Social SAQ scheme — `SOCSAQ_<n>_<sub>` (newer; some monitorings use it).
 *
 * - PA04 / PA11 sub-questions route per the Product-confirmed question-level
 *   map (discovery §6 Q-6 amendment, 2026-06-10). The SAQ sub-question order
 *   differs from the audit's, so `SOCSAQ_4_*` / `SOCSAQ_11_*` are routed from the
 *   SAQ question labels rather than the audit override.
 * - Every other PA routes mechanically to its single impact area.
 * - Gender-Based Violence is intentionally unused in v1 (no PA04 sub-question
 *   matches it). An unknown questionKey is handled by the finding-writer.
 *
 * The values are `companies_service_impact_areas.id`. They were the legacy
 * `impact_areas` random ids until the 2026-09 remap; this map is a *writer* of
 * `audits_service_audit_findings.impact_area_id`, so leaving it on the old
 * vocabulary would have re-introduced legacy ids after every sync. An
 * integration test asserts every value here still exists in that table, which
 * is what catches a stale entry rather than a silently unresolvable finding.
 */
export const BSCI_QUESTION_TO_IMPACT_AREA: Record<string, string> = {
  FU_MAN_PA10_1: 'CONTRACTS_AND_TERMS_OF_EMPLOYMENT',
  FU_MAN_PA10_2: 'CONTRACTS_AND_TERMS_OF_EMPLOYMENT',
  FU_MAN_PA10_3: 'CONTRACTS_AND_TERMS_OF_EMPLOYMENT',
  FU_MAN_PA10_4: 'CONTRACTS_AND_TERMS_OF_EMPLOYMENT',
  FU_MAN_PA11_1: 'FORCED_LABOUR',
  FU_MAN_PA11_2: 'MODERN_SLAVERY',
  FU_MAN_PA11_3: 'HARRASSMENT_AND_ABUSE',
  FU_MAN_PA11_4: 'MODERN_SLAVERY',
  FU_MAN_PA12_1: 'ENVIRONMENTAL',
  FU_MAN_PA12_2: 'ENVIRONMENTAL',
  FU_MAN_PA12_3: 'ENVIRONMENTAL',
  FU_MAN_PA12_4: 'ENVIRONMENTAL',
  FU_MAN_PA12_5: 'ENVIRONMENTAL',
  FU_MAN_PA13_1: 'RESPONSIBLE_BUSINESS_CONDUCT',
  FU_MAN_PA13_2: 'RESPONSIBLE_BUSINESS_CONDUCT',
  FU_MAN_PA13_3: 'RESPONSIBLE_BUSINESS_CONDUCT',
  FU_MAN_PA13_4: 'RESPONSIBLE_BUSINESS_CONDUCT',
  FU_MAN_PA13_5: 'RESPONSIBLE_BUSINESS_CONDUCT',
  FU_MAN_PA1_1: 'MANAGEMENT_SYSTEMS',
  FU_MAN_PA1_2: 'MANAGEMENT_SYSTEMS',
  FU_MAN_PA1_3: 'MANAGEMENT_SYSTEMS',
  FU_MAN_PA1_4: 'MANAGEMENT_SYSTEMS',
  FU_MAN_PA1_5: 'MANAGEMENT_SYSTEMS',
  FU_MAN_PA1_6: 'MANAGEMENT_SYSTEMS',
  FU_MAN_PA1_7: 'MANAGEMENT_SYSTEMS',
  FU_MAN_PA1_8: 'MANAGEMENT_SYSTEMS',
  FU_MAN_PA2_1: 'WORKER_INVOLVEMENT',
  FU_MAN_PA2_2: 'WORKER_INVOLVEMENT',
  FU_MAN_PA2_3: 'WORKER_INVOLVEMENT',
  FU_MAN_PA2_4: 'WORKER_INVOLVEMENT',
  FU_MAN_PA2_5: 'WORKER_INVOLVEMENT',
  FU_MAN_PA3_1: 'FREEDOM_OF_ASSOCIATION_AND_COLLECTIVE_BARGAINING',
  FU_MAN_PA3_2: 'FREEDOM_OF_ASSOCIATION_AND_COLLECTIVE_BARGAINING',
  FU_MAN_PA3_3: 'FREEDOM_OF_ASSOCIATION_AND_COLLECTIVE_BARGAINING',
  FU_MAN_PA3_4: 'FREEDOM_OF_ASSOCIATION_AND_COLLECTIVE_BARGAINING',
  FU_MAN_PA4_1: 'DISCRIMINATION',
  FU_MAN_PA4_2: 'HARRASSMENT_AND_ABUSE',
  FU_MAN_PA4_3: 'DISCRIMINATION',
  FU_MAN_PA5_1: 'WAGES_AND_BENEFITS',
  FU_MAN_PA5_2: 'WAGES_AND_BENEFITS',
  FU_MAN_PA5_3: 'WAGES_AND_BENEFITS',
  FU_MAN_PA5_4: 'WAGES_AND_BENEFITS',
  FU_MAN_PA5_5: 'WAGES_AND_BENEFITS',
  FU_MAN_PA5_6: 'WAGES_AND_BENEFITS',
  FU_MAN_PA6_1: 'WORKING_HOURS_AND_OVERTIME',
  FU_MAN_PA6_2: 'WORKING_HOURS_AND_OVERTIME',
  FU_MAN_PA6_3: 'WORKING_HOURS_AND_OVERTIME',
  FU_MAN_PA6_4: 'WORKING_HOURS_AND_OVERTIME',
  FU_MAN_PA7_1: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_10: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_11: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_12: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_13: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_14: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_15: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_16: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_17: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_18: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_19: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_2: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_20: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_21: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_22: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_23: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_24: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_25: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_3: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_4: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_5: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_6: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_7: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_8: 'HEALTH_AND_SAFETY',
  FU_MAN_PA7_9: 'HEALTH_AND_SAFETY',
  FU_MAN_PA8_1: 'CHILD_LABOR',
  FU_MAN_PA8_2: 'CHILD_LABOR',
  FU_MAN_PA8_3: 'CHILD_LABOR',
  FU_MAN_PA8_4: 'CHILD_LABOR',
  FU_MAN_PA9_1: 'VULNERABLE_GROUP',
  FU_MAN_PA9_2: 'VULNERABLE_GROUP',
  FU_MAN_PA9_3: 'VULNERABLE_GROUP',
  FU_MAN_PA9_4: 'VULNERABLE_GROUP',
  FU_MAN_PA9_5: 'VULNERABLE_GROUP',
  FU_MAN_PA9_6: 'VULNERABLE_GROUP',
  MAN_PA10_1: 'CONTRACTS_AND_TERMS_OF_EMPLOYMENT',
  MAN_PA10_2: 'CONTRACTS_AND_TERMS_OF_EMPLOYMENT',
  MAN_PA10_3: 'CONTRACTS_AND_TERMS_OF_EMPLOYMENT',
  MAN_PA10_4: 'CONTRACTS_AND_TERMS_OF_EMPLOYMENT',
  MAN_PA11_1: 'FORCED_LABOUR',
  MAN_PA11_2: 'MODERN_SLAVERY',
  MAN_PA11_3: 'HARRASSMENT_AND_ABUSE',
  MAN_PA11_4: 'MODERN_SLAVERY',
  MAN_PA12_1: 'ENVIRONMENTAL',
  MAN_PA12_2: 'ENVIRONMENTAL',
  MAN_PA12_3: 'ENVIRONMENTAL',
  MAN_PA12_4: 'ENVIRONMENTAL',
  MAN_PA12_5: 'ENVIRONMENTAL',
  MAN_PA13_1: 'RESPONSIBLE_BUSINESS_CONDUCT',
  MAN_PA13_2: 'RESPONSIBLE_BUSINESS_CONDUCT',
  MAN_PA13_3: 'RESPONSIBLE_BUSINESS_CONDUCT',
  MAN_PA13_4: 'RESPONSIBLE_BUSINESS_CONDUCT',
  MAN_PA13_5: 'RESPONSIBLE_BUSINESS_CONDUCT',
  MAN_PA1_1: 'MANAGEMENT_SYSTEMS',
  MAN_PA1_2: 'MANAGEMENT_SYSTEMS',
  MAN_PA1_3: 'MANAGEMENT_SYSTEMS',
  MAN_PA1_4: 'MANAGEMENT_SYSTEMS',
  MAN_PA1_5: 'MANAGEMENT_SYSTEMS',
  MAN_PA1_6: 'MANAGEMENT_SYSTEMS',
  MAN_PA1_7: 'MANAGEMENT_SYSTEMS',
  MAN_PA1_8: 'MANAGEMENT_SYSTEMS',
  MAN_PA2_1: 'WORKER_INVOLVEMENT',
  MAN_PA2_2: 'WORKER_INVOLVEMENT',
  MAN_PA2_3: 'WORKER_INVOLVEMENT',
  MAN_PA2_4: 'WORKER_INVOLVEMENT',
  MAN_PA2_5: 'WORKER_INVOLVEMENT',
  MAN_PA3_1: 'FREEDOM_OF_ASSOCIATION_AND_COLLECTIVE_BARGAINING',
  MAN_PA3_2: 'FREEDOM_OF_ASSOCIATION_AND_COLLECTIVE_BARGAINING',
  MAN_PA3_3: 'FREEDOM_OF_ASSOCIATION_AND_COLLECTIVE_BARGAINING',
  MAN_PA3_4: 'FREEDOM_OF_ASSOCIATION_AND_COLLECTIVE_BARGAINING',
  MAN_PA4_1: 'DISCRIMINATION',
  MAN_PA4_2: 'HARRASSMENT_AND_ABUSE',
  MAN_PA4_3: 'DISCRIMINATION',
  MAN_PA5_1: 'WAGES_AND_BENEFITS',
  MAN_PA5_2: 'WAGES_AND_BENEFITS',
  MAN_PA5_3: 'WAGES_AND_BENEFITS',
  MAN_PA5_4: 'WAGES_AND_BENEFITS',
  MAN_PA5_5: 'WAGES_AND_BENEFITS',
  MAN_PA5_6: 'WAGES_AND_BENEFITS',
  MAN_PA6_1: 'WORKING_HOURS_AND_OVERTIME',
  MAN_PA6_2: 'WORKING_HOURS_AND_OVERTIME',
  MAN_PA6_3: 'WORKING_HOURS_AND_OVERTIME',
  MAN_PA6_4: 'WORKING_HOURS_AND_OVERTIME',
  MAN_PA7_1: 'HEALTH_AND_SAFETY',
  MAN_PA7_10: 'HEALTH_AND_SAFETY',
  MAN_PA7_11: 'HEALTH_AND_SAFETY',
  MAN_PA7_12: 'HEALTH_AND_SAFETY',
  MAN_PA7_13: 'HEALTH_AND_SAFETY',
  MAN_PA7_14: 'HEALTH_AND_SAFETY',
  MAN_PA7_15: 'HEALTH_AND_SAFETY',
  MAN_PA7_16: 'HEALTH_AND_SAFETY',
  MAN_PA7_17: 'HEALTH_AND_SAFETY',
  MAN_PA7_18: 'HEALTH_AND_SAFETY',
  MAN_PA7_19: 'HEALTH_AND_SAFETY',
  MAN_PA7_2: 'HEALTH_AND_SAFETY',
  MAN_PA7_20: 'HEALTH_AND_SAFETY',
  MAN_PA7_21: 'HEALTH_AND_SAFETY',
  MAN_PA7_22: 'HEALTH_AND_SAFETY',
  MAN_PA7_23: 'HEALTH_AND_SAFETY',
  MAN_PA7_24: 'HEALTH_AND_SAFETY',
  MAN_PA7_25: 'HEALTH_AND_SAFETY',
  MAN_PA7_3: 'HEALTH_AND_SAFETY',
  MAN_PA7_4: 'HEALTH_AND_SAFETY',
  MAN_PA7_5: 'HEALTH_AND_SAFETY',
  MAN_PA7_6: 'HEALTH_AND_SAFETY',
  MAN_PA7_7: 'HEALTH_AND_SAFETY',
  MAN_PA7_8: 'HEALTH_AND_SAFETY',
  MAN_PA7_9: 'HEALTH_AND_SAFETY',
  MAN_PA8_1: 'CHILD_LABOR',
  MAN_PA8_2: 'CHILD_LABOR',
  MAN_PA8_3: 'CHILD_LABOR',
  MAN_PA8_4: 'CHILD_LABOR',
  MAN_PA9_1: 'VULNERABLE_GROUP',
  MAN_PA9_2: 'VULNERABLE_GROUP',
  MAN_PA9_3: 'VULNERABLE_GROUP',
  MAN_PA9_4: 'VULNERABLE_GROUP',
  MAN_PA9_5: 'VULNERABLE_GROUP',
  MAN_PA9_6: 'VULNERABLE_GROUP',
  MAN_SPA_PA10_1: 'CONTRACTS_AND_TERMS_OF_EMPLOYMENT',
  MAN_SPA_PA10_2: 'CONTRACTS_AND_TERMS_OF_EMPLOYMENT',
  MAN_SPA_PA11_1: 'FORCED_LABOUR',
  MAN_SPA_PA11_2: 'MODERN_SLAVERY',
  MAN_SPA_PA12_1: 'ENVIRONMENTAL',
  MAN_SPA_PA12_2: 'ENVIRONMENTAL',
  MAN_SPA_PA13_1: 'RESPONSIBLE_BUSINESS_CONDUCT',
  MAN_SPA_PA13_2: 'RESPONSIBLE_BUSINESS_CONDUCT',
  MAN_SPA_PA1_1: 'MANAGEMENT_SYSTEMS',
  MAN_SPA_PA1_2: 'MANAGEMENT_SYSTEMS',
  MAN_SPA_PA1_3: 'MANAGEMENT_SYSTEMS',
  MAN_SPA_PA2_1: 'WORKER_INVOLVEMENT',
  MAN_SPA_PA2_2: 'WORKER_INVOLVEMENT',
  MAN_SPA_PA3_1: 'FREEDOM_OF_ASSOCIATION_AND_COLLECTIVE_BARGAINING',
  MAN_SPA_PA3_2: 'FREEDOM_OF_ASSOCIATION_AND_COLLECTIVE_BARGAINING',
  MAN_SPA_PA4_1: 'DISCRIMINATION',
  MAN_SPA_PA4_2: 'HARRASSMENT_AND_ABUSE',
  MAN_SPA_PA5_1: 'WAGES_AND_BENEFITS',
  MAN_SPA_PA5_2: 'WAGES_AND_BENEFITS',
  MAN_SPA_PA5_3: 'WAGES_AND_BENEFITS',
  MAN_SPA_PA5_4: 'WAGES_AND_BENEFITS',
  MAN_SPA_PA6_1: 'WORKING_HOURS_AND_OVERTIME',
  MAN_SPA_PA6_2: 'WORKING_HOURS_AND_OVERTIME',
  MAN_SPA_PA6_3: 'WORKING_HOURS_AND_OVERTIME',
  MAN_SPA_PA6_4: 'WORKING_HOURS_AND_OVERTIME',
  MAN_SPA_PA7_1: 'HEALTH_AND_SAFETY',
  MAN_SPA_PA7_10: 'HEALTH_AND_SAFETY',
  MAN_SPA_PA7_11: 'HEALTH_AND_SAFETY',
  MAN_SPA_PA7_12: 'HEALTH_AND_SAFETY',
  MAN_SPA_PA7_13: 'HEALTH_AND_SAFETY',
  MAN_SPA_PA7_14: 'HEALTH_AND_SAFETY',
  MAN_SPA_PA7_15: 'HEALTH_AND_SAFETY',
  MAN_SPA_PA7_16: 'HEALTH_AND_SAFETY',
  MAN_SPA_PA7_2: 'HEALTH_AND_SAFETY',
  MAN_SPA_PA7_3: 'HEALTH_AND_SAFETY',
  MAN_SPA_PA7_4: 'HEALTH_AND_SAFETY',
  MAN_SPA_PA7_5: 'HEALTH_AND_SAFETY',
  MAN_SPA_PA7_6: 'HEALTH_AND_SAFETY',
  MAN_SPA_PA7_7: 'HEALTH_AND_SAFETY',
  MAN_SPA_PA7_8: 'HEALTH_AND_SAFETY',
  MAN_SPA_PA7_9: 'HEALTH_AND_SAFETY',
  MAN_SPA_PA8_1: 'CHILD_LABOR',
  MAN_SPA_PA8_2: 'CHILD_LABOR',
  MAN_SPA_PA9_1: 'VULNERABLE_GROUP',
  MAN_SPA_PA9_2: 'VULNERABLE_GROUP',
  MAN_SPA_PA9_3: 'VULNERABLE_GROUP',
  MAN_SPA_PA9_4: 'VULNERABLE_GROUP',
  // amfori Social SAQ scheme (SOCSAQ_<pa>_<sub>) — the newer questionnaire some
  // BSCI monitorings deliver their findings under. Mirrors the same 13
  // Performance Areas as the MAN_* audit keys, so each routes to the PA's impact
  // area. NB: the PA4/PA11 sub-question order differs from the audit scheme, so
  // those sub-questions are routed from the SAQ labels (not the MAN override).
  SOCSAQ_1_1: 'MANAGEMENT_SYSTEMS',
  SOCSAQ_1_2: 'MANAGEMENT_SYSTEMS',
  SOCSAQ_1_3: 'MANAGEMENT_SYSTEMS',
  SOCSAQ_1_4: 'MANAGEMENT_SYSTEMS',
  SOCSAQ_1_5: 'MANAGEMENT_SYSTEMS',
  SOCSAQ_1_6: 'MANAGEMENT_SYSTEMS',
  SOCSAQ_1_7: 'MANAGEMENT_SYSTEMS',
  SOCSAQ_2_1: 'WORKER_INVOLVEMENT',
  SOCSAQ_2_2: 'WORKER_INVOLVEMENT',
  SOCSAQ_2_3: 'WORKER_INVOLVEMENT',
  SOCSAQ_2_4: 'WORKER_INVOLVEMENT',
  SOCSAQ_2_5: 'WORKER_INVOLVEMENT',
  SOCSAQ_3_1: 'FREEDOM_OF_ASSOCIATION_AND_COLLECTIVE_BARGAINING',
  SOCSAQ_3_2: 'FREEDOM_OF_ASSOCIATION_AND_COLLECTIVE_BARGAINING',
  SOCSAQ_3_3: 'FREEDOM_OF_ASSOCIATION_AND_COLLECTIVE_BARGAINING',
  SOCSAQ_3_4: 'FREEDOM_OF_ASSOCIATION_AND_COLLECTIVE_BARGAINING',
  // PA4 (No Discrimination, Violence or Harassment) — SAQ sub-question routing.
  SOCSAQ_4_1: 'DISCRIMINATION',
  SOCSAQ_4_2: 'DISCRIMINATION',
  SOCSAQ_4_3: 'HARRASSMENT_AND_ABUSE',
  SOCSAQ_5_1: 'WAGES_AND_BENEFITS',
  SOCSAQ_5_2: 'WAGES_AND_BENEFITS',
  SOCSAQ_5_3: 'WAGES_AND_BENEFITS',
  SOCSAQ_5_4: 'WAGES_AND_BENEFITS',
  SOCSAQ_5_5: 'WAGES_AND_BENEFITS',
  SOCSAQ_5_6: 'WAGES_AND_BENEFITS',
  SOCSAQ_6_1: 'WORKING_HOURS_AND_OVERTIME',
  SOCSAQ_6_2: 'WORKING_HOURS_AND_OVERTIME',
  SOCSAQ_6_3: 'WORKING_HOURS_AND_OVERTIME',
  SOCSAQ_6_4: 'WORKING_HOURS_AND_OVERTIME',
  SOCSAQ_7_1: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_2: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_3: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_4: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_5: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_6: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_7: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_8: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_9: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_10: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_11: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_12: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_13: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_14: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_15: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_16: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_17: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_18: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_19: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_20: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_21: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_22: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_23: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_24: 'HEALTH_AND_SAFETY',
  SOCSAQ_7_25: 'HEALTH_AND_SAFETY',
  SOCSAQ_8_1: 'CHILD_LABOR',
  SOCSAQ_8_2: 'CHILD_LABOR',
  SOCSAQ_8_3: 'CHILD_LABOR',
  SOCSAQ_8_4: 'CHILD_LABOR',
  SOCSAQ_9_1: 'VULNERABLE_GROUP',
  SOCSAQ_9_2: 'VULNERABLE_GROUP',
  SOCSAQ_9_3: 'VULNERABLE_GROUP',
  SOCSAQ_9_4: 'VULNERABLE_GROUP',
  SOCSAQ_9_5: 'VULNERABLE_GROUP',
  SOCSAQ_9_6: 'VULNERABLE_GROUP',
  SOCSAQ_10_1: 'CONTRACTS_AND_TERMS_OF_EMPLOYMENT',
  SOCSAQ_10_2: 'CONTRACTS_AND_TERMS_OF_EMPLOYMENT',
  SOCSAQ_10_3: 'CONTRACTS_AND_TERMS_OF_EMPLOYMENT',
  SOCSAQ_10_4: 'CONTRACTS_AND_TERMS_OF_EMPLOYMENT',
  // PA11 (No Bonded, Forced Labour or Human Trafficking) — SAQ sub-question routing.
  SOCSAQ_11_1: 'FORCED_LABOUR',
  SOCSAQ_11_2: 'MODERN_SLAVERY',
  SOCSAQ_11_3: 'HARRASSMENT_AND_ABUSE',
  // 11.4 "establishes disciplinary procedures in writing … explains them clearly
  // to workers" — fair/transparent disciplinary practice → Harassment & Abuse
  // (not Modern Slavery, which was only the audit scheme's PA11 catch-all).
  SOCSAQ_11_4: 'HARRASSMENT_AND_ABUSE',
  SOCSAQ_12_1: 'ENVIRONMENTAL',
  SOCSAQ_12_2: 'ENVIRONMENTAL',
  SOCSAQ_12_3: 'ENVIRONMENTAL',
  SOCSAQ_12_4: 'ENVIRONMENTAL',
  SOCSAQ_12_5: 'ENVIRONMENTAL',
  SOCSAQ_13_1: 'RESPONSIBLE_BUSINESS_CONDUCT',
  SOCSAQ_13_2: 'RESPONSIBLE_BUSINESS_CONDUCT',
  SOCSAQ_13_3: 'RESPONSIBLE_BUSINESS_CONDUCT',
  SOCSAQ_13_4: 'RESPONSIBLE_BUSINESS_CONDUCT',
};

/**
 * amfori Zero-Tolerance type code -> human-readable label. Ships with the only
 * type observed in live API data; unknown codes fall back to `ztTypeLabel`'s
 * title-caser so a new type never crashes the import.
 */
export const AMFORI_ZERO_TOLERANCE_TYPE_LABELS: Record<string, string> = {
  UNETHICAL_BEHAVIOR: 'Unethical Behavior',
};
